/* eslint-disable max-classes-per-file */
import { googleWebClientId } from 'config/constants';
import { isImportableMime } from 'helpers/import-mime';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type DriveFile = {
  id: string;
  name: string;
  mimeType: string;
  size?: number;
  modifiedTime?: string;
  createdTime?: string;
  iconLink?: string;
  thumbnailLink?: string;
  // EXIF capture time for photos ("YYYY:MM:DD HH:MM:SS"); Drive has no
  // equivalent for videos.
  takenAt?: string;
  isFolder: boolean;
  isImportable: boolean;
};

export type DriveListResponse = {
  files: DriveFile[];
  nextPageToken?: string;
};

export type DownloadedDriveFile = {
  blob: Blob;
  name: string;
  mimeType: string;
  size: number;
  modifiedTime?: string;
  createdTime?: string;
};

export const DRIVE_FOLDER_MIME = 'application/vnd.google-apps.folder';

// Shared with the other import sources; re-exported for compatibility.
export { IMPORTABLE_MIMES, isImportableMime } from 'helpers/import-mime';

export const isFolderMime = (mime: string) => mime === DRIVE_FOLDER_MIME;

const escapeDriveString = (s: string) => s.replace(/'/g, "\\'");

const FILE_FIELDS =
  'id,name,mimeType,size,modifiedTime,createdTime,iconLink,thumbnailLink,imageMediaMetadata(time)';

const normalizeFile = (raw: any): DriveFile => {
  const mimeType = raw.mimeType as string;
  return {
    id: raw.id,
    name: raw.name,
    mimeType,
    size: raw.size != null ? Number(raw.size) : undefined,
    modifiedTime: raw.modifiedTime,
    createdTime: raw.createdTime,
    iconLink: raw.iconLink,
    thumbnailLink: raw.thumbnailLink,
    takenAt: raw.imageMediaMetadata?.time,
    isFolder: isFolderMime(mimeType),
    isImportable: isImportableMime(mimeType),
  };
};

// ---------------------------------------------------------------------------
// Google Identity Services (GIS) auth
// ---------------------------------------------------------------------------

export const DRIVE_SCOPE = 'https://www.googleapis.com/auth/drive.readonly';
// Only the files the user picks in Google's own picker. Needs no restricted
// scope review, unlike DRIVE_SCOPE.
export const DRIVE_FILE_SCOPE = 'https://www.googleapis.com/auth/drive.file';

const GIS_SRC = 'https://accounts.google.com/gsi/client';

declare global {
  interface Window {
    google?: any;
  }
}

let gisLoadPromise: Promise<void> | null = null;

const loadGisScript = (): Promise<void> => {
  if (typeof window === 'undefined') return Promise.resolve();
  if (window.google?.accounts?.oauth2) return Promise.resolve();
  if (gisLoadPromise) return gisLoadPromise;

  gisLoadPromise = new Promise<void>((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>(
      `script[src="${GIS_SRC}"]`,
    );
    if (existing) {
      existing.addEventListener('load', () => resolve(), { once: true });
      existing.addEventListener(
        'error',
        () => reject(new Error('Failed to load Google Identity Services')),
        { once: true },
      );
      return;
    }
    const script = document.createElement('script');
    script.src = GIS_SRC;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = () =>
      reject(new Error('Failed to load Google Identity Services'));
    document.head.appendChild(script);
  }).catch((err) => {
    gisLoadPromise = null;
    throw err;
  });

  return gisLoadPromise;
};

export class DriveAuthCancelled extends Error {
  constructor() {
    super('Google sign-in cancelled');
    this.name = 'DriveAuthCancelled';
  }
}

// The browser blocked Google's sign-in popup (no recent click, e.g. after a
// reload). A cancel to the existing callers; the picker offers a button.
export class DrivePopupBlocked extends DriveAuthCancelled {
  constructor() {
    super();
    this.name = 'DrivePopupBlocked';
  }
}

export type DriveAccount = {
  email: string;
  name?: string;
  photoUrl?: string;
};

let cachedAccessToken: string | null = null;
// The account behind cachedAccessToken (see getDriveAccount).
let cachedAccount: { token: string; account: DriveAccount } | null = null;
let cachedTokenExpiresAt = 0;
let tokenClient: any = null;
let driveScope = DRIVE_SCOPE;

/** Which scope later sign-ins ask for; a change drops the cached token. */
export const setDriveScope = (scope: string) => {
  if (scope === driveScope) return;
  driveScope = scope;
  cachedAccessToken = null;
  cachedTokenExpiresAt = 0;
  tokenClient = null;
};

const ensureTokenClient = async () => {
  await loadGisScript();
  if (!googleWebClientId) {
    throw new Error('VITE_APP_GOOGLE_WEB_CLIENT_ID is not configured');
  }
  // TEMP debug: confirm which client ID and origin Google will see
  // eslint-disable-next-line no-console
  console.info(
    '[drive] client_id=',
    googleWebClientId,
    'origin=',
    window.location.origin,
  );
  if (!tokenClient) {
    tokenClient = window.google.accounts.oauth2.initTokenClient({
      client_id: googleWebClientId,
      scope: driveScope,
      callback: () => {},
    });
  }
  return tokenClient;
};

const requestToken = (
  prompt: '' | 'consent' | 'select_account',
): Promise<string> =>
  new Promise<string>((resolve, reject) => {
    let settled = false;
    ensureTokenClient()
      .then((client) => {
        const activeClient = Object.assign(client, {
          callback: (response: any) => {
            if (settled) return;
            settled = true;
            if (response?.error) {
              if (
                response.error === 'access_denied' ||
                response.error === 'popup_closed' ||
                response.error === 'user_cancel' ||
                response.error === 'popup_closed_by_user'
              ) {
                reject(new DriveAuthCancelled());
                return;
              }
              reject(
                new Error(
                  response.error_description ||
                    response.error ||
                    'Google sign-in failed',
                ),
              );
              return;
            }
            if (!response?.access_token) {
              reject(new Error('No access token returned'));
              return;
            }
            cachedAccessToken = response.access_token;
            const expiresIn = Number(response.expires_in) || 3600;
            // Refresh a bit early to avoid edge-of-expiry races.
            cachedTokenExpiresAt = Date.now() + (expiresIn - 60) * 1000;
            resolve(cachedAccessToken!);
          },
          error_callback: (err: any) => {
            if (settled) return;
            settled = true;
            if (err?.type === 'popup_failed_to_open') {
              reject(new DrivePopupBlocked());
              return;
            }
            if (err?.type === 'popup_closed') {
              reject(new DriveAuthCancelled());
              return;
            }
            reject(new Error(err?.message || 'Google sign-in failed'));
          },
        });
        activeClient.requestAccessToken({ prompt });
      })
      .catch(reject);
  });

/**
 * Try silent access-token refresh first; on failure fall back to interactive.
 * User-cancel surfaces as DriveAuthCancelled.
 */
export const signInToDrive = async (): Promise<string> => {
  if (cachedAccessToken && Date.now() < cachedTokenExpiresAt) {
    return cachedAccessToken;
  }
  try {
    return await requestToken('');
  } catch (err) {
    if (err instanceof DriveAuthCancelled) throw err;
    return requestToken('consent');
  }
};

export const signOutFromDrive = (): Promise<void> =>
  new Promise<void>((resolve) => {
    const token = cachedAccessToken;
    cachedAccessToken = null;
    cachedTokenExpiresAt = 0;
    tokenClient = null;
    if (!token || !window.google?.accounts?.oauth2?.revoke) {
      resolve();
      return;
    }
    try {
      window.google.accounts.oauth2.revoke(token, () => resolve());
    } catch {
      resolve();
    }
  });

export const getCachedAccessToken = (): string | null => cachedAccessToken;

/**
 * Shows Google's account chooser so the user can connect another account.
 * Backing out keeps the current account (its token stays cached).
 */
export const switchDriveAccount = (): Promise<string> =>
  requestToken('select_account');

// ---------------------------------------------------------------------------
// Drive REST calls
// ---------------------------------------------------------------------------

const DRIVE_BASE = 'https://www.googleapis.com/drive/v3';

const driveFetch = async (
  path: string,
  params?: Record<string, string | undefined>,
  init?: RequestInit,
): Promise<Response> => {
  const token = await signInToDrive();
  const url = new URL(`${DRIVE_BASE}${path}`);
  if (params) {
    Object.entries(params).forEach(([k, v]) => {
      if (v != null) url.searchParams.set(k, v);
    });
  }
  const response = await fetch(url.toString(), {
    ...init,
    headers: {
      ...(init?.headers || {}),
      Authorization: `Bearer ${token}`,
    },
  });
  if (!response.ok) {
    let snippet = '';
    try {
      snippet = (await response.text()).slice(0, 240);
    } catch {
      // ignore
    }
    throw new Error(
      `Drive API ${response.status} ${response.statusText}: ${snippet}`,
    );
  }
  return response;
};

/**
 * The Google account Drive is connected to, or null when nobody is signed in.
 * Never opens a sign-in popup. Works with the drive.file scope too.
 */
export const getDriveAccount = async (): Promise<DriveAccount | null> => {
  const token = cachedAccessToken;
  if (!token || Date.now() >= cachedTokenExpiresAt) return null;
  if (cachedAccount?.token === token) return cachedAccount.account;

  const response = await driveFetch('/about', {
    fields: 'user(displayName,emailAddress,photoLink)',
  });
  const { user } = await response.json();
  const account: DriveAccount = {
    email: user?.emailAddress ?? '',
    name: user?.displayName,
    photoUrl: user?.photoLink,
  };
  cachedAccount = { token, account };
  return account;
};

export const listFolder = async (
  folderId: string,
  pageToken?: string,
): Promise<DriveListResponse> => {
  const response = await driveFetch('/files', {
    q: `'${escapeDriveString(folderId)}' in parents and trashed = false`,
    fields: `nextPageToken, files(${FILE_FIELDS})`,
    pageSize: '200',
    orderBy: 'folder,name',
    spaces: 'drive',
    pageToken,
  });
  const data = await response.json();
  return {
    files: (data.files || []).map(normalizeFile),
    nextPageToken: data.nextPageToken,
  };
};

/**
 * BFS over a folder, returning every importable (non-Google-doc) file
 * inside it and its subfolders.
 */
export const listFolderRecursive = async (
  folderId: string,
): Promise<DriveFile[]> => {
  const importable: DriveFile[] = [];
  const queue: string[] = [folderId];
  while (queue.length > 0) {
    const currentId = queue.shift()!;
    let pageToken: string | undefined;
    do {
      // eslint-disable-next-line no-await-in-loop
      const page = await listFolder(currentId, pageToken);
      page.files.forEach((file) => {
        if (file.isFolder) {
          queue.push(file.id);
        } else if (file.isImportable) {
          importable.push(file);
        }
      });
      pageToken = page.nextPageToken;
    } while (pageToken);
  }
  return importable;
};

export const searchDrive = async (
  query: string,
  pageToken?: string,
  parentId?: string,
): Promise<DriveListResponse> => {
  const escaped = escapeDriveString(query);
  let q = `name contains '${escaped}' and trashed = false`;
  if (parentId) {
    q += ` and '${escapeDriveString(parentId)}' in parents`;
  }
  const response = await driveFetch('/files', {
    q,
    fields: `nextPageToken, files(${FILE_FIELDS})`,
    pageSize: '100',
    orderBy: 'folder,name',
    spaces: 'drive',
    pageToken,
  });
  const data = await response.json();
  return {
    files: (data.files || []).map(normalizeFile),
    nextPageToken: data.nextPageToken,
  };
};

export const getFileMeta = async (id: string): Promise<DriveFile> => {
  const response = await driveFetch(`/files/${encodeURIComponent(id)}`, {
    fields: FILE_FIELDS,
  });
  const data = await response.json();
  return normalizeFile(data);
};

export const downloadDriveFile = async (
  file: DriveFile,
): Promise<DownloadedDriveFile> => {
  const response = await driveFetch(`/files/${encodeURIComponent(file.id)}`, {
    alt: 'media',
  });
  const blob = await response.blob();
  return {
    blob,
    name: file.name,
    mimeType: file.mimeType,
    size: blob.size,
    modifiedTime: file.modifiedTime,
    createdTime: file.createdTime,
  };
};
